// vg-binding: P7.D-18
// vg-binding: P7.D-24
//
// apps/vendor/src/pages/AddProduct.tsx
//
// Phase 7 Wave 8 Task 28 — Add Product wizard.
//
// Flow:
//   1. Pick a submission method (catalog | file | api).
//   2. Catalog: enter blueprint id + CommonFields + VariantMatrix → POST.
//      File:    paste CSV → POST /products/import-excel.
//      API:     show cURL example (no FE submit).
//   3. On submit success, navigate back to /products.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageHeader, useVendorStaffRole } from '@printway/ui';

import { useTranslation } from '../lib/i18n/index.js';
import { ApiInstructions } from '../features/products/add/ApiInstructions.js';
import { CatalogPicker } from '../features/products/add/CatalogPicker.js';
import {
  COMMON_FIELDS_DEFAULT,
  CommonFields,
  type CommonFieldsValue,
} from '../features/products/add/CommonFields.js';
import { FileImporter } from '../features/products/add/FileImporter.js';
import { MethodPicker } from '../features/products/add/MethodPicker.js';
import { type VariantDraft } from '../features/products/variants/types.js';
import { VariantOptionSelector } from '../features/products/variants/VariantOptionSelector.js';
import {
  useSubmitProduct,
  useVendorCatalogBlueprintDetail,
  type SubmissionMethod,
} from '../features/products/api.js';

export function AddProductPage(): JSX.Element {
  const { t } = useTranslation('products');
  const navigate = useNavigate();
  //  (Amendment #6) — default to 'file' so the Excel/CSV importer
  // shows on mount; catalog/api hidden in MethodPicker (BE support intact).
  const [method, setMethod] = useState<SubmissionMethod | null>('file');
  const [commonFields, setCommonFields] = useState<CommonFieldsValue>(
    COMMON_FIELDS_DEFAULT,
  );
  const [sourceId, setSourceId] = useState('');
  // Amendment #4 — catalog path: chosen option values per axis +
  // the generated variant drafts from VariantOptionSelector.
  const [catalogChosen, setCatalogChosen] = useState<Record<string, string[]>>({});
  const [catalogVariants, setCatalogVariants] = useState<VariantDraft[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const submitM = useSubmitProduct();
  // Same rule as the API (`IMPORT_ROLES`, routes/products.ts): only the owner
  // and operators create products from a file; other staff would only meet a
  // 403 after picking a file (R1-10).
  const { role } = useVendorStaffRole();
  const canImport = role === 'owner' || role === 'operator';

  // Amendment #4 — load blueprint detail (variants + option axes)
  // once a blueprint is chosen so the option selector can render.
  const blueprintDetailQ = useVendorCatalogBlueprintDetail(
    method === 'catalog' && sourceId.trim().length > 0 ? sourceId : undefined,
  );

  const enabledCatalogVariants = catalogVariants.filter((v) => v.enabled);

  // dbg-vendor-catalog-submit-disabled — surface WHY the submit gate is
  // closed so the user knows which field is still missing.
  const missingCatalogFields: string[] = [];
  if (method === 'catalog') {
    if (sourceId.trim().length === 0) missingCatalogFields.push(t('add.fields.blueprint'));
    if (commonFields.name.trim().length === 0) missingCatalogFields.push(t('add.fields.name'));
    if (commonFields.sku_prefix.trim().length === 0) missingCatalogFields.push(t('add.fields.skuPrefix'));
    if (commonFields.product_type.trim().length === 0) missingCatalogFields.push(t('add.fields.productType'));
    if (enabledCatalogVariants.length === 0)
      missingCatalogFields.push(t('add.fields.minSku'));
  }
  const canSubmitCatalog =
    method === 'catalog' && missingCatalogFields.length === 0;

  async function handleSubmit(): Promise<void> {
    if (!method || method !== 'catalog') return;
    setErrorMsg(null);
    try {
      await submitM.mutateAsync({
        method,
        source_id: sourceId,
        name: commonFields.name,
        sku_prefix: commonFields.sku_prefix,
        product_type: commonFields.product_type,
        cost_price_cents: commonFields.cost_price_cents,
        // Bug 7 + Amendment #4 — dimensions + per-SKU ops sent per-variant below.
        warehouses_assigned: [],
        low_stock_threshold: commonFields.low_stock_threshold,
        variants: enabledCatalogVariants.map((v) => ({
          size: v.size,
          color: v.color,
          enabled: v.enabled,
          cost_markup_cents: v.cost_markup_cents,
          image_urls: v.image_urls.filter((u) => u.trim().length > 0),
          dimensions: v.dimensions,
          processing_time_days: v.processing_time_days,
          capacity_per_day: v.capacity_per_day,
          weight_grams: v.weight_grams,
          ...(v.external_sku && v.external_sku.trim().length > 0
            ? { external_sku: v.external_sku.trim() }
            : {}),
          ...(v.option_value_ids ? { option_value_ids: v.option_value_ids } : {}),
          ...(v.weight_grams_override !== undefined
            ? { weight_grams_override: v.weight_grams_override }
            : {}),
        })),
      });
      navigate('/products');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : t('add.submitFailed'));
    }
  }

  return (
    <main className="flex flex-col gap-4 px-6 py-6">
      <PageHeader
        title={t('add.pageTitle')}
        actions={
          <Link to="/products" className="text-sm text-pw-primary hover:underline">
            {t('add.backToList')}
          </Link>
        }
      />

      <MethodPicker active={method} onPick={setMethod} />

      {method === 'catalog' ? (
        <>
          <CatalogPicker source_id={sourceId} onChange={setSourceId} />
          <section className="rounded-xl border border-pw-border bg-pw-bg p-4">
            <h3 className="mb-3 text-sm font-semibold text-pw-text">
              {t('add.commonInfo')}
            </h3>
            <CommonFields value={commonFields} onChange={setCommonFields} />
          </section>
          {/* Amendment #4 — blueprint-driven variant generation.
              Pick option values; SKUs/dimensions/weight default read-only from
              the blueprint. Free-type VariantMatrix is for file/api only. */}
          {sourceId.trim().length === 0 ? (
            <p className="text-xs text-pw-text-muted" data-testid="catalog-pick-first">
              {t('optionSelector.pickBlueprintFirst')}
            </p>
          ) : blueprintDetailQ.isLoading ? (
            <p className="text-xs text-pw-text-muted">{t('catalog.loading')}</p>
          ) : blueprintDetailQ.isError ? (
            <p className="text-xs text-red-700" data-testid="catalog-detail-error">
              {blueprintDetailQ.error.userMessage ?? blueprintDetailQ.error.message}
            </p>
          ) : blueprintDetailQ.data ? (
            <VariantOptionSelector
              detail={blueprintDetailQ.data}
              value={catalogVariants}
              onChange={setCatalogVariants}
              chosen={catalogChosen}
              onChosenChange={setCatalogChosen}
            />
          ) : null}

          {errorMsg ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {errorMsg}
            </p>
          ) : null}

          <div className="flex flex-col items-end gap-2">
            {missingCatalogFields.length > 0 ? (
              <p
                data-testid="add-product-missing-fields"
                className="text-xs text-amber-700"
              >
                {t('add.missingFields', { fields: missingCatalogFields.join(', ') })}.
              </p>
            ) : null}
            <button
              type="button"
              data-testid="add-product-submit"
              disabled={!canSubmitCatalog || submitM.isPending}
              onClick={() => void handleSubmit()}
              className="inline-flex h-10 items-center rounded-md bg-pw-primary px-5 text-sm font-semibold text-white hover:bg-pw-primary-strong disabled:cursor-not-allowed disabled:opacity-50"
              title={
                missingCatalogFields.length > 0
                  ? t('add.missingFields', { fields: missingCatalogFields.join(', ') })
                  : undefined
              }
            >
              {submitM.isPending ? t('add.submitting') : t('add.submit')}
            </button>
          </div>
        </>
      ) : null}

      {method === 'file' ? (
        canImport ? (
          <FileImporter vendorId="" />
        ) : (
          <p
            data-testid="add-product-file-no-permission"
            className="rounded-md border border-pw-border bg-pw-surface px-3 py-2 text-sm text-pw-text-muted"
          >
            {t('fileImporter.noPermission')}
          </p>
        )
      ) : null}

      {method === 'api' ? <ApiInstructions /> : null}
    </main>
  );
}

export default AddProductPage;
